<template>
  <MinimalLayout
    :title="(step === 'confirm' || step === 'password') && t('signup.title')"
    :loading="step === 'init'"
  >
    <template v-if="step === 'confirm'">
      <p class="mb-4 text-center text-sm">{{ t("signup.confirmDescription", { email }) }}</p>
      <v-btn
        :disabled="loading"
        :loading="loading"
        block
        color="primary"
        size="large"
        variant="flat"
        @click="signIn"
      >
        {{ t("signup.confirm") }}
      </v-btn>
    </template>

    <template v-else-if="step === 'password'">
      <v-form
        ref="form"
        :disabled="loading"
        validate-on="invalid-input lazy"
        @submit.prevent="submitPassword"
      >
        <v-text-field
          :model-value="email"
          :rules="[required, validEmail]"
          clearable
          disabled
          :label="t('signup.email')"
          class="mb-2"
          variant="outlined"
        ></v-text-field>

        <v-text-field
          id="new-password"
          v-model="password"
          autocomplete="new-password"
          :rules="[required, minLength(12)]"
          clearable
          :label="t('signup.password')"
          :type="showPassword ? 'text' : 'password'"
          class="mb-2"
          variant="outlined"
          :append-inner-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
          @click:append-inner="showPassword = !showPassword"
        ></v-text-field>

        <v-alert v-if="errorMessage" density="compact" class="mb-3" color="error" type="error">
          <span>{{ errorMessage }}</span>
        </v-alert>

        <v-btn
          :disabled="loading"
          :loading="loading"
          block
          color="primary"
          size="large"
          type="submit"
          variant="flat"
        >
          {{ t("signup.next") }}
        </v-btn>
      </v-form>
    </template>

    <ExpiredSentStatus v-else-if="step === 'linkExpired'" :message="t('signup.linkExpired')" />

    <ErrorStatus v-else>
      <span>{{ errorMessage }}</span>
      <span v-if="errorMessage === t('signup.accountAlreadyExists')">
        &nbsp;<a href="/login">{{ t("signup.signIn") }}</a></span
      ></ErrorStatus
    >
  </MinimalLayout>
</template>

<script lang="ts" setup>
import ErrorStatus from "@/components/ErrorStatus.vue";
import ExpiredSentStatus from "@/components/ExpiredSentStatus.vue";
import MinimalLayout from "@/components/MinimalLayout.vue";
import { useI18n } from "@/composables/useI18n";
import { useLanguage } from "@/composables/useLanguage";
import { useRules } from "@/composables/useRules";
import { call } from "@/firebase";
import { useUserStore } from "@/store";
import { useFirebaseErrorMessage } from "@/utils/errors";
import { captureException } from "@sentry/vue";
import { FirebaseError } from "firebase/app";
import { AuthErrorCodes, signInWithEmailLink, updatePassword } from "firebase/auth";
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useFirebaseAuth } from "vuefire";
import type { VForm } from "vuetify/components";

const { t } = useI18n();
const { currentLanguage } = useLanguage();
const auth = useFirebaseAuth();
const route = useRoute();
const router = useRouter();
const user = useUserStore();
const password = ref("");
const showPassword = ref(false);
const loading = ref(false);
const form = ref<VForm>();
const { getFirebaseErrorMessage } = useFirebaseErrorMessage();
const { required, validEmail, minLength } = useRules();
const email = computed(() => {
  return typeof route.query.email === "string" ? route.query.email : null;
});
const step = ref<"init" | "confirm" | "password" | "linkExpired" | "error">("init");
const errorMessage = ref<string>("");

onMounted(async () => {
  if (!email.value) {
    const error = new Error("Email is not defined in query params");
    console.error(error);
    captureException(error);
    errorMessage.value = t("signup.error");
    step.value = "error";
    return;
  }

  // The oobCode is single-use: wait for a user click so email scanners prefetching the link don't burn it
  if (route.query.oobCode) {
    step.value = "confirm";
    return;
  }

  if (await resendUserInvitationEmailHandler()) {
    step.value = "linkExpired";
    return;
  }
  errorMessage.value = t("signup.error");
  step.value = "error";
});

const signIn = async () => {
  loading.value = true;
  try {
    if (!auth) throw new Error("Auth is not defined");
    if (!email.value) throw new Error("Email is not defined in query params");

    console.log("Signing in with email link");
    await signInWithEmailLink(auth, email.value);

    console.log("Proceeding to password setup");
    step.value = "password";
  } catch (cause) {
    if (cause instanceof FirebaseError) {
      if (cause.code === AuthErrorCodes.MFA_REQUIRED) {
        console.log("User needs multi-factor authentication, redirecting to login");
        void router.push({ name: "Login" });
        return;
      }

      if (
        cause.code === AuthErrorCodes.INVALID_OOB_CODE ||
        cause.code === AuthErrorCodes.EXPIRED_OOB_CODE
      ) {
        if (await resendUserInvitationEmailHandler()) {
          step.value = "linkExpired";
          return;
        }
      } else {
        errorMessage.value = getFirebaseErrorMessage(cause);
      }
    }
    if (!errorMessage.value) errorMessage.value = t("signup.error");
    step.value = "error";
    const error = new Error("Error signing in with email link", { cause });
    console.error(error);
    captureException(error);
  } finally {
    loading.value = false;
  }
};

const resendUserInvitationEmailHandler = async () => {
  try {
    if (!email.value) throw new Error("Email is not defined in query params");
    console.log("Resending user invitation email");
    await call("resendUserInvitationEmail")({
      email: email.value,
      lang: currentLanguage.value.code,
    });
    return true;
  } catch (cause) {
    const error = new Error("Error resending user invitation email", { cause });
    console.error(error);
    captureException(error);
    return false;
  }
};

const submitPassword = async () => {
  const validateForm = await form.value?.validate();
  if (!validateForm?.valid) return;
  if (!user.authenticatedUser) return;

  loading.value = true;
  try {
    console.log("Updating password");
    await updatePassword(user.authenticatedUser.firebaseUser, password.value);
  } catch (cause) {
    const error = new Error("Error updating password", { cause });
    console.error(error);
    captureException(error);
    if (cause instanceof FirebaseError) {
      errorMessage.value = getFirebaseErrorMessage(cause);
    } else {
      errorMessage.value = t("signup.error");
    }
    loading.value = false;
    return;
  }
  console.log("Password updated successfully, redirecting to MFA setup");
  void router.push({ name: "Mfa" });
};
</script>
